/**
 * SwapAnimator — Orchestrates animated asset swaps with cross-fade + Y-translate.
 *
 * Visual effect: old asset fades out and slides down, new asset fades in and
 * slides up. A configurable overlap creates a smooth cross-transition.
 *
 * Uses two RevealController instances (one per asset) — does not contain
 * its own opacity logic.
 *
 * @see docs/ACTIVE_TASK.md — Teil 4: Swap Animation
 */

import { RevealController } from './RevealController';
import type { RevealObject } from './RevealController';
import type { SwapOptions } from '../api/types/objectManager';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Wraps a Y-translatable group for the slide effect. */
export interface SwapGroup {
  /** The group's current Y-translation (world units). */
  position: { y: number };
}

/** Internal state for one side of the swap (old or new). */
interface SwapSide {
  reveal: RevealController;
  group: SwapGroup;
  /** Base Y-position before animation. */
  baseY: number;
  /** +1 for slide up, -1 for slide down. */
  slideDirection: number;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_DURATION = 800;
const DEFAULT_SLIDE_DISTANCE = 0.15; // meters
const DEFAULT_OVERLAP = 0.3;

// ---------------------------------------------------------------------------
// Easing helpers — match RevealController
// ---------------------------------------------------------------------------

function easeInOutCubic(t: number): number {
  return t < 0.5
    ? 4 * t * t * t
    : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// ---------------------------------------------------------------------------
// SwapAnimator
// ---------------------------------------------------------------------------

export class SwapAnimator {
  private _oldSide: SwapSide | null = null;
  private _newSide: SwapSide | null = null;

  private _totalDuration = DEFAULT_DURATION;
  private _slideDistance = DEFAULT_SLIDE_DISTANCE;
  private _overlap = DEFAULT_OVERLAP;
  private _elapsed = 0;
  private _active = false;
  private _disposed = false;
  private _onComplete: (() => void) | null = null;

  /** Whether a swap animation is currently running. */
  get isAnimating(): boolean {
    return this._active;
  }

  /**
   * Start an animated swap transition.
   *
   * @param oldScene — The scene being swapped OUT (may be null for first-time show).
   * @param oldGroup — The group whose position.y will be slid down.
   * @param newScene — The scene being swapped IN.
   * @param newGroup — The group whose position.y will be slid up from below.
   * @param options — Swap animation options.
   */
  start(
    oldScene: RevealObject | null,
    oldGroup: SwapGroup | null,
    newScene: RevealObject,
    newGroup: SwapGroup,
    options: SwapOptions = {},
  ): void {
    if (this._disposed) return;

    // Cancel any running swap
    this._cancel();

    this._totalDuration = Math.max(options.duration ?? DEFAULT_DURATION, 1);
    this._slideDistance = options.slideDistance ?? DEFAULT_SLIDE_DISTANCE;
    this._overlap = Math.max(0, Math.min(1, options.overlap ?? DEFAULT_OVERLAP));
    this._elapsed = 0;
    this._onComplete = options.onComplete ?? null;

    // --- Old side (fading out + sliding down) ---
    if (oldScene && oldGroup) {
      const oldReveal = new RevealController();
      oldReveal.attach(oldScene);
      this._oldSide = {
        reveal: oldReveal,
        group: oldGroup,
        baseY: oldGroup.position.y,
        slideDirection: -1, // slide down
      };
    } else {
      this._oldSide = null;
    }

    // --- New side (fading in + sliding up from below) ---
    const newReveal = new RevealController();
    newReveal.attach(newScene);
    newReveal.setImmediate(0); // start invisible
    newGroup.position.y = (this._newSide?.baseY ?? newGroup.position.y) - this._slideDistance;

    this._newSide = {
      reveal: newReveal,
      group: newGroup,
      baseY: newGroup.position.y + this._slideDistance, // target Y
      slideDirection: 1, // slide up
    };
    // Position at start state
    newGroup.position.y = this._newSide.baseY - this._slideDistance;

    this._active = true;
  }

  /**
   * Per-frame update. Call from useFrame.
   * @param deltaMs — elapsed time since last frame in milliseconds.
   * @returns `true` while animation is running.
   */
  tick(deltaMs: number): boolean {
    if (!this._active || this._disposed) return false;

    this._elapsed += deltaMs;
    const t = Math.min(this._elapsed / this._totalDuration, 1);

    // Compute phase progress for old and new sides.
    // The overlap defines how much the two phases run in parallel.
    //
    // Timeline layout (overlap factor 0→1):
    //   overlap=0 → sequential: old runs 0→0.5, new runs 0.5→1
    //   overlap=0.3 → 30% overlap: old runs 0→0.65, new runs 0.35→1
    //   overlap=1 → fully parallel: old runs 0→1, new runs 0→1

    const halfOverlap = this._overlap * 0.5;
    const oldEnd = 0.5 + halfOverlap;   // 0.5 … 1.0
    const newStart = 0.5 - halfOverlap; // 0.5 … 0.0

    // --- Old side ---
    if (this._oldSide) {
      const oldT = Math.min(t / oldEnd, 1);
      const oldEased = easeInOutCubic(oldT);

      // Opacity: 1 → 0
      this._oldSide.reveal.setImmediate(1 - oldEased);
      // Y position: baseY → baseY - slideDistance
      this._oldSide.group.position.y =
        this._oldSide.baseY + this._oldSide.slideDirection * this._slideDistance * oldEased;
    }

    // --- New side ---
    if (this._newSide) {
      const rawNewT = (t - newStart) / (1 - newStart);
      const newT = Math.max(0, Math.min(rawNewT, 1));
      const newEased = easeInOutCubic(newT);

      // Opacity: 0 → 1
      this._newSide.reveal.setImmediate(newEased);
      // Y position: baseY - slideDistance → baseY
      this._newSide.group.position.y =
        this._newSide.baseY - this._slideDistance * (1 - newEased);
    }

    // --- Check completion ---
    if (t >= 1) {
      this._finalize();
    }

    return this._active;
  }

  /**
   * Get the RevealController for the OLD side (for cleanup/disposal).
   * Available while a swap is active or after it completes.
   */
  get oldReveal(): RevealController | null {
    return this._oldSide?.reveal ?? null;
  }

  /**
   * Get the RevealController for the NEW side.
   */
  get newReveal(): RevealController | null {
    return this._newSide?.reveal ?? null;
  }

  /** Cancel any running animation and dispose controllers. */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this._cancel();
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  /** Finalize after animation completes. */
  private _finalize(): void {
    this._active = false;

    // Finalize old side — dispose restores original material state
    // (materials go back to snapshot values for potential cache reuse)
    if (this._oldSide) {
      this._oldSide.reveal.dispose();
    }

    // Finalize new side — ensure fully visible at correct position
    if (this._newSide) {
      this._newSide.reveal.setImmediate(1);
      this._newSide.group.position.y = this._newSide.baseY;
    }

    const cb = this._onComplete;
    this._onComplete = null;
    this._oldSide = null;
    // Keep _newSide for newReveal getter
    cb?.();
  }

  /** Cancel running animation without finalizing. */
  private _cancel(): void {
    this._active = false;
    this._oldSide?.reveal.dispose();
    this._newSide?.reveal.dispose();
    this._oldSide = null;
    this._newSide = null;
    this._onComplete = null;
  }
}
